import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import { updatePageMeta } from '../lib/seo';
import img1 from "../assets/aboutimages/img1.jpg";
import img2 from "../assets/aboutimages/img2.jpg";
import img3 from "../assets/aboutimages/img3.jpg";
import img4 from "../assets/aboutimages/img4.jpg";
import img5 from "../assets/aboutimages/img5.jpg";
import img6 from "../assets/aboutimages/img6.jpg";

gsap.registerPlugin(ScrollTrigger);

// ✅ Placeholder images (no CORS issues)
const images = {
  mission: img1,
  vision: img2,
  values: img3,
  capabilities: img4,
  story: img5,
  team: img6,
};

function About() {
  const sectionRefs = useRef([]);
  sectionRefs.current = [];

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  useEffect(() => {
    updatePageMeta({
      title: 'About Us - EnviroTech Solutions',
      description:
        'Learn about our mission, expertise, and commitment to environmental excellence with ISO/NABL accredited services.',
    });

    // Premium section animations with subtle parallax
    sectionRefs.current.forEach((section) => {
      if (section) {
        gsap.fromTo(
          section,
          { autoAlpha: 0, y: 20 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 88%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    });

    // Premium reveal-item animations with stagger
    const revealItems = document.querySelectorAll('.reveal-item');
    revealItems.forEach((item, index) => {
      gsap.fromTo(
        item,
        { opacity: 0, y: 20, scale: 0.98 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 92%',
            toggleActions: 'play none none none',
          },
        }
      );
    });

    // Subtle parallax for images
    gsap.utils.toArray('.about-image').forEach((img, index) => {
      gsap.to(img, {
        y: (index % 2 === 0 ? 15 : -15),
        ease: 'none',
        scrollTrigger: {
          trigger: img.closest('section') || img,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.5,
        },
      });
    });

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'About' },
  ];

  const values = [
    { title: 'Quality Excellence', description: 'Maintaining highest standards in testing and analysis through rigorous quality control.', icon: '⭐' },
    { title: 'Technical Competence', description: 'Employing qualified professionals and state-of-the-art instrumentation.', icon: '🎓' },
    { title: 'Customer Focus', description: 'Understanding client needs and delivering tailored environmental solutions.', icon: '🤝' },
    { title: 'Sustainability', description: 'Promoting eco-friendly practices and resource conservation.', icon: '🌱' },
    { title: 'Innovation', description: 'Adopting cutting-edge technologies and methodologies.', icon: '💡' },
    { title: 'Integrity', description: 'Operating with transparency, honesty, and ethical practices.', icon: '✓' },
  ];

  const capabilities = [
    {
      title: 'Laboratory Infrastructure',
      items: [
        'Advanced analytical instruments',
        'Controlled testing environments',
        'Sample storage facilities',
        'Quality assurance systems',
      ],
    },
    {
      title: 'Technical Expertise',
      items: [
        'Environmental engineers',
        'Analytical chemists',
        'Microbiologists',
        'Safety consultants',
      ],
    },
    {
      title: 'Service Coverage',
      items: [
        'Pan-India operations',
        'Mobile testing units',
        'Onsite sampling',
        'Remote consultation',
      ],
    },
    {
      title: 'Industry Experience',
      items: [
        'Manufacturing sector',
        'Healthcare facilities',
        'Construction projects',
        'Government departments',
      ],
    },
  ];

  return (
    <>
      <PageHeader
        title="About EnviroTech Solutions"
        description="Your trusted partner in environmental testing, consultancy, and sustainable solutions"
        breadcrumbs={breadcrumbs}
      />

      {/* Mission & Vision */}
      <Section ref={addToRefs}>
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 mb-12">
            {/* Mission */}
            <div className="flex flex-col justify-center order-2 lg:order-1">
              <span className="text-xs font-medium text-primary-600 uppercase tracking-wider mb-3">Our Purpose</span>
              <h2 className="text-2xl md:text-3xl font-bold text-accent-900 mb-3 leading-tight tracking-tight">
                Our Mission
              </h2>
              <div className="w-12 h-0.5 bg-gradient-to-r from-primary-600 to-primary-400 mb-4"></div>
              <p className="text-sm md:text-base text-accent-700 leading-relaxed">
              Our mission is to deliver accurate, reliable, and timely testing services using advanced technology and skilled expertise. We are committed to supporting environmental compliance, protecting public health, and creating awareness about environmental responsibility.
              </p>
            </div>
            <div className="reveal-item order-1 lg:order-2">
              <div className="relative overflow-hidden rounded-lg shadow-sm border border-accent-200 h-[240px] md:h-[280px] lg:h-[320px]">
                <img
                  src={images.mission}
                  alt="Mission"
                  className="about-image w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Vision */}
            <div className="reveal-item">
              <div className="relative overflow-hidden rounded-lg shadow-sm border border-accent-200 h-[240px] md:h-[280px] lg:h-[320px]">
                <img
                  src={images.vision}
                  alt="Vision"
                  className="about-image w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-xs font-medium text-secondary-600 uppercase tracking-wider mb-3">Our Aspiration</span>
              <h2 className="text-2xl md:text-3xl font-bold text-accent-900 mb-3 leading-tight tracking-tight">
                Our Vision
              </h2>
              <div className="w-12 h-0.5 bg-gradient-to-r from-secondary-600 to-secondary-400 mb-4"></div>
              <p className="text-sm md:text-base text-accent-700 leading-relaxed">
              To become a trusted and innovative pollution testing laboratory that ensures environmental safety and supports sustainable development through scientific excellence and ethical practices.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Core Values */}
      <Section ref={addToRefs} title="Our Core Values" subtitle="The principles that guide our work and relationships" background="gray">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 reveal-item">
            <div className="relative overflow-hidden rounded-lg shadow-sm border border-accent-200 h-48 md:h-56 lg:h-64">
              <img
                src={images.values}
                alt="Core Values"
                className="about-image w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {values.map((value, index) => (
              <div
                key={index}
                className="reveal-item bg-white p-5 md:p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-accent-200 hover:border-primary-300 hover:-translate-y-0.5"
              >
                <div className="flex flex-col items-center text-center h-full">
                  <div className="text-3xl md:text-4xl mb-3">{value.icon}</div>
                  <h3 className="text-base md:text-lg font-semibold text-accent-900 mb-2 leading-tight">
                    {value.title}
                  </h3>
                  <p className="text-xs md:text-sm text-accent-600 leading-relaxed flex-grow">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Capabilities */}
      <Section ref={addToRefs} title="Our Capabilities" subtitle="Infrastructure, expertise, and reach to serve your environmental needs">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 reveal-item">
            <div className="relative overflow-hidden rounded-lg shadow-sm border border-accent-200 h-48 md:h-56 lg:h-64">
              <img
                src={images.capabilities}
                alt="Capabilities"
                className="about-image w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="reveal-item bg-white p-5 md:p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border-l-2 border-primary-600 hover:border-primary-700"
              >
                <h3 className="text-base md:text-lg font-semibold text-accent-900 mb-4 leading-tight">
                  {capability.title}
                </h3>
                <ul className="space-y-2">
                  {capability.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-accent-700">
                      <div className="flex-shrink-0 mt-0.5 mr-2.5">
                        <svg
                          className="w-4 h-4 text-primary-600"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-xs md:text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Story */}
      <Section ref={addToRefs} background="pattern">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 reveal-item">
            <h2 className="text-2xl md:text-3xl font-bold text-accent-900 mb-4 tracking-tight">Our Story</h2>
            <div className="relative overflow-hidden rounded-lg shadow-sm border border-accent-200 max-w-3xl mx-auto h-48 md:h-56 lg:h-64">
              <img
                src={images.story}
                alt="Our Story"
                className="about-image w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="max-w-3xl mx-auto space-y-3 md:space-y-4">
            <div className="reveal-item bg-white/95 backdrop-blur-sm rounded-lg p-4 md:p-5 shadow-sm border border-accent-200">
              <p className="text-sm md:text-base text-accent-700 leading-relaxed">
              Terra Environ Labs Pvt. Ltd. is a professional environmental testing laboratory established to provide accurate, reliable, and timely analytical services. With a state-of-the-art laboratory, advanced instrumentation, and experienced personnel, we serve industries, commercial establishments, and government sectors across Telangana. Our services include water, wastewater, soil, air quality, and environmental monitoring, supporting regulatory compliance and sustainable environmental management.
              </p>
            </div>
           
          </div>
        </div>
      </Section>

      {/* Team */}
      <Section ref={addToRefs} title="Multidisciplinary Expertise">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 reveal-item">
            <div className="relative overflow-hidden rounded-lg shadow-sm border border-accent-200 h-48 md:h-56 lg:h-64">
              <img
                src={images.team}
                alt="Our Team"
                className="about-image w-full h-full object-cover"
              />
            </div>
          </div>
          <p className="text-sm md:text-base text-accent-700 mb-8 text-center max-w-2xl mx-auto leading-relaxed reveal-item">
            Our strength lies in our diverse team of professionals who bring specialized
            knowledge from various environmental disciplines.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { role: 'Environmental Engineers', description: 'Design treatment systems, conduct impact assessments, and develop sustainable solutions.' },
              { role: 'Analytical Chemists', description: 'Perform complex chemical analysis using advanced instrumentation and validated methods.' },
              { role: 'Microbiologists', description: 'Conduct biological testing for water, food, and environmental samples.' },
              { role: 'Safety Consultants', description: 'Assess workplace hazards and ensure compliance with health and safety regulations.' },
              { role: 'Regulatory Specialists', description: 'Navigate environmental laws and assist with permits, clearances, and compliance.' },
              { role: 'Quality Assurance Team', description: 'Maintain laboratory standards and ensure accuracy through rigorous quality control.' },
            ].map((member, index) => (
              <div 
                key={index} 
                className="reveal-item bg-white rounded-lg p-4 md:p-5 border-l-2 border-primary-600 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
              >
                <h3 className="text-sm md:text-base font-semibold text-accent-900 mb-2 leading-tight">
                  {member.role}
                </h3>
                <p className="text-xs md:text-sm text-accent-600 leading-relaxed">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}

export default About;
